/* eslint-disable max-lines */
import * as React from 'react'
import classNames from 'classnames'
import ResizeObserver from 'rc-resize-observer'
import omit from 'rc-util/lib/omit'

import { saveDataByUid, getDataByUid } from '@/hooks/useRecordOperation/store'
import { addObserveTarget, getFixedBottom, getFixedTop, getTargetRect, removeObserveTarget } from './utils'

import CollapseWrapper from './components/CollapseWrapper'
import styles from './index.less'

function getDefaultTarget() {
    return typeof window !== 'undefined' ? window : null
}

// Affix
export interface AffixProps {
    /** 距离窗口顶部达到指定偏移量后触发 */
    offsetTop?: number
    /** 距离窗口底部达到指定偏移量后触发 */
    offsetBottom?: number
    style?: React.CSSProperties
    /** 固定状态改变时触发的回调函数 */
    onChange?: (affixed?: boolean) => void
    /** 设置 Affix 需要监听其滚动事件的元素，值为一个返回对应 DOM 元素的函数 */
    target?: () => Window | HTMLElement | null
    prefixCls?: string
    className?: string
    recordKey?: string
    children: React.ReactNode
}

interface InternalAffixProps extends AffixProps {
    affixPrefixCls: string
}

enum AffixStatus {
    None,
    Prepare
}

const transStyleObjToString = (obj: any) => {
    return Object.keys(obj)
        .reduce((t, c) => {
            const v = typeof obj[c] == 'number' ? `${obj[c]}px` : obj[c]

            const str: string = `${c}:${v};`

            t.push(str)

            return t
        }, [])
        .join('')
}

export interface AffixState {
    affixStyle?: React.CSSProperties
    placeholderStyle?: React.CSSProperties
    extraPlaceholderStyle?: React.CSSProperties
    status: AffixStatus
    lastAffix: boolean
    collapsed: boolean
    prevTarget: Window | HTMLElement | null
}

class Affix extends React.Component<InternalAffixProps, AffixState> {
    state: AffixState = {
        status: AffixStatus.None,
        lastAffix: false,
        prevTarget: null,
        collapsed: false,
        userCollapsed: false,
        affixUserCollapsed: false
    }

    placeholderNode: HTMLDivElement

    collapsedViewNode: HTMLDivElement

    fixedNode: HTMLDivElement

    private timeout: any

    private scrollDirection: any

    private lastScrollTop: any

    private _methods: any = {}

    context: any
    private toggleCollapsedView: any

    private getTargetFunc() {
        const { getTargetContainer } = this.context
        const { target } = this.props

        if (target !== undefined) {
            return target
        }

        return getTargetContainer || getDefaultTarget
    }

    // Event handler
    componentDidMount() {
        const targetFunc = this.getTargetFunc()
        if (targetFunc) {
            // [Legacy] Wait for parent component ref has its value.
            // We should use target as directly element instead of function which makes element check hard.
            this.timeout = setTimeout(() => {
                addObserveTarget(targetFunc(), this)
                // Mock Event object.
                this.updatePosition()
            })
        }
    }

    componentDidUpdate(prevProps: AffixProps, preState: any) {
        const { prevTarget } = this.state
        const targetFunc = this.getTargetFunc()
        const newTarget = targetFunc?.() || null

        if (prevTarget !== newTarget) {
            removeObserveTarget(this)
            if (newTarget) {
                addObserveTarget(newTarget, this)
                // Mock Event object.
                this.updatePosition()
            }

            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ prevTarget: newTarget })
        }

        if (
            prevProps.offsetTop !== this.props.offsetTop ||
            prevProps.offsetBottom !== this.props.offsetBottom ||
            preState.collapsed !== this.state.collapsed
        ) {
            this.updatePosition()
        }

        this.measure()
    }

    componentWillUnmount() {
        clearTimeout(this.timeout)
        removeObserveTarget(this)
        this.updatePosition && this.updatePosition.cancel && this.updatePosition.cancel()
        // https://github.com/ant-design/ant-design/issues/22683
        this.lazyUpdatePosition && this.lazyUpdatePosition.cancel && this.lazyUpdatePosition.cancel()
    }

    getOffsetTop = () => {
        const { offsetBottom, offsetTop } = this.props
        return offsetBottom === undefined && offsetTop === undefined ? 0 : offsetTop
    }

    getOffsetBottom = () => this.props.offsetBottom

    savePlaceholderNode = (node: HTMLDivElement) => {
        this.placeholderNode = node
    }

    saveCollapsedViewNode = (node: HTMLDivElement) => {
        this.collapsedViewNode = node
    }

    saveFixedNode = (node: HTMLDivElement) => {
        this.fixedNode = node
    }

    saveExtraPlaceholder = (node: HTMLDivElement) => {
        this.extraPlaceholderNode = node
    }

    // js直接更新元素的style
    // collapsed, affixStyle, placeholderStyle
    updateNodeStyles = (newState: any) => {
        Object.keys(newState).forEach(key => {
            const value = newState[key]

            if (key === 'collapsed') {
                this.toggleCollapsedView && this.toggleCollapsedView(value)
                return
            }

            if (key === 'affixStyle') {

                this.fixedNode && this.fixedNode.setAttribute('style', transStyleObjToString(value))
                return
            }

            if (key === 'hasExtraPlaceholder') {
                if (value) {
                    this.extraPlaceholderNode?.classList.add(styles.hide)
                } else {
                    this.extraPlaceholderNode?.classList.remove(styles.hide)
                }
                return
            }
        })
    }

    // 注册
    registerMethods = (obj: any) => {
        Object.keys(obj).forEach(v => {
            this._methods[v] = obj[v]
        })
    }

    // 卸载
    unRegisterMethods = (keys: string[]) => {
        keys.forEach(v => {
            delete this._methods[v]
        })
    }

    triggerMethod = (key: string, ...args: any) => {
        this._methods[key] && this._methods[key](...args)
    }

    // 手动切换
    collapse = (collapsed: boolean) => {
        const { recordKey } = this.props
        const { lastAffix } = this.state

        if (collapsed) {
            const cacheKey = `${recordKey}_collapse`

            const record = getDataByUid(cacheKey)

            if (!record) {
                this.triggerMethod('toggleFieldsConfigTip', true)

                saveDataByUid(cacheKey, 1)
                return
            } else {
                this.triggerMethod('toggleFieldsConfigTip', false)
            }
        }

        this.setState({
            collapsed,
            userCollapsed: collapsed, // 用户手动切换
            affixUserCollapsed: lastAffix && collapsed // 吸顶状态用户点击收起
        })
    }

    // =================== Measure ===================
    measure = () => {
        const { lastScrollTop } = this
        const { status, lastAffix, collapsed, userCollapsed, affixUserCollapsed } = this.state
        const { onChange } = this.props
        const targetFunc = this.getTargetFunc()
        if (status !== AffixStatus.Prepare || !this.fixedNode || !this.placeholderNode || !targetFunc) {
            return
        }

        const offsetTop = this.getOffsetTop()
        const offsetBottom = this.getOffsetBottom()

        const targetNode = targetFunc()
        if (!targetNode) {
            return
        }

        const newState: Partial<AffixState> | any = {
            status: AffixStatus.None
        }

        const scrollTop = targetNode.scrollTop
        this.scrollDirection = scrollTop - lastScrollTop > 0 ? 'up' : 'down'
        this.lastScrollTop = scrollTop

        const targetRect = getTargetRect(targetNode)
        const placeholderReact = getTargetRect(this.placeholderNode)
        const collapsedViewNodeRect = getTargetRect(this.collapsedViewNode)
        const subviewRect = this.getSubviewRect()

        const fixedTop = getFixedTop({
            placeholderReact,
            targetRect,
            offsetTop,
            collapsed,
            lastAffix,
            userCollapsed,
            subviewRect,
            collapsedViewNodeRect
        })
        const fixedBottom = getFixedBottom(placeholderReact, targetRect, offsetBottom)

        if (fixedTop !== undefined) {
            if (!lastAffix) {
                newState.collapsed = true

                newState.extraPlaceholderStyle = {
                    width: placeholderReact.width,
                    height: subviewRect?.height || 0
                }

                this.triggerMethod('toggleFieldsConfigTip', false)
            }
            newState.affixStyle = {
                position: 'fixed',
                top: fixedTop,
                width: placeholderReact.width,
                height: placeholderReact.height
            }
            newState.placeholderStyle = {
                width: placeholderReact.width,
                height: placeholderReact.height
            }
        } else if (fixedBottom !== undefined) {
            newState.affixStyle = {
                position: 'fixed',
                bottom: fixedBottom,
                width: placeholderReact.width,
                height: placeholderReact.height
            }
            newState.placeholderStyle = {
                width: placeholderReact.width,
                height: placeholderReact.height
            }
        }

        if ((fixedTop === undefined && !userCollapsed) || (fixedTop === undefined && affixUserCollapsed)) {
            newState.collapsed = false
        }

        newState.lastAffix = !!newState.affixStyle
        if (onChange && lastAffix !== newState.lastAffix) {
            onChange(newState.lastAffix)
        }

        this.updateNodeStyles({
            ...newState,
            hasExtraPlaceholder: !!newState.affixStyle
        })
        this.setState(newState as AffixState)
    }

    // @ts-ignore TS6133
    prepareMeasure = () => {
        // event param is used before. Keep compatible ts define here.
        this.setState({
            status: AffixStatus.Prepare,
            affixStyle: undefined,
            placeholderStyle: undefined
        })

        // Test if `updatePosition` called
        if (process.env.NODE_ENV === 'test') {
            const { onTestUpdatePosition } = this.props as any
            onTestUpdatePosition?.()
        }
    }

    // Handle realign logic
    // @throttleByAnimationFrameDecorator()
    updatePosition() {
        this.prepareMeasure()
    }

    // @throttleByAnimationFrameDecorator()
    lazyUpdatePosition() {
        const { lastScrollTop } = this
        const targetFunc = this.getTargetFunc()
        const { affixStyle, collapsed, lastAffix, userCollapsed } = this.state

        // Check position change before measure to make Safari smooth
        if (targetFunc && affixStyle) {
            const offsetTop = this.getOffsetTop()
            const offsetBottom = this.getOffsetBottom()

            const targetNode = targetFunc()
            if (targetNode && this.placeholderNode) {
                const scrollTop = targetNode.scrollTop
                this.scrollDirection = scrollTop - lastScrollTop > 0 ? 'up' : 'down'
                this.lastScrollTop = scrollTop

                const targetRect = getTargetRect(targetNode)
                const placeholderReact = getTargetRect(this.placeholderNode)
                const collapsedViewNodeRect = getTargetRect(this.collapsedViewNode)
                const subviewRect = this.getSubviewRect()

                const fixedTop = getFixedTop({
                    placeholderReact,
                    targetRect,
                    offsetTop,
                    collapsed,
                    lastAffix,
                    subviewRect,
                    userCollapsed,
                    collapsedViewNodeRect
                })
                const fixedBottom = getFixedBottom(placeholderReact, targetRect, offsetBottom)

                if (fixedTop !== undefined && affixStyle.top === fixedTop) {
                    if (!collapsed && this.scrollDirection === 'up') {
                        this.triggerMethod('toggleFieldsConfigTip', false)
                        this.setState({
                            collapsed: true
                        })
                    }
                    return
                }

                if (fixedBottom !== undefined && affixStyle.bottom === fixedBottom) {
                    return
                }
            }
        }

        // Directly call prepare measure since it's already throttled.
        this.prepareMeasure()
    }

    onHandle = ({ fold, expand, toggle, getSubviewRect }) => {
        this.foldCollapsedView = fold
        this.expandCollapsedView = expand
        this.toggleCollapsedView = toggle
        this.getSubviewRect = getSubviewRect
    }

    // =================== Render ===================
    render() {
        const { affixStyle, placeholderStyle, extraPlaceholderStyle, collapsed, userCollapsed } = this.state
        const { affixPrefixCls, children, collapsedView, onSizeChange } = this.props
        const className = classNames({
            [affixPrefixCls]: !!affixStyle
        })

        let props = omit(this.props, [
            'prefixCls',
            'offsetTop',
            'offsetBottom',
            'target',
            'collapsedView',
            'onChange',
            'affixPrefixCls'
        ])
        // Omit this since `onTestUpdatePosition` only works on test.
        if (process.env.NODE_ENV === 'test') {
            props = omit(props as typeof props & { onTestUpdatePosition: any }, ['onTestUpdatePosition'])
        }

        const clonedView = collapsedView
            ? React.cloneElement(collapsedView, {
                  registerMethods: this.registerMethods,
                  unRegisterMethods: this.unRegisterMethods
              })
            : collapsedView

        return (
            <>
                {collapsedView ? (
                    <div
                        ref={this.saveExtraPlaceholder}
                        style={collapsed && !userCollapsed ? extraPlaceholderStyle : {}}
                    />
                ) : null}
                <ResizeObserver
                    onResize={e => {
                        this.updatePosition()

                        onSizeChange && onSizeChange(e)
                    }}>
                    <div {...props} ref={this.savePlaceholderNode}>
                        {affixStyle && <div style={placeholderStyle} aria-hidden="true" />}
                        <div className={className} ref={this.saveFixedNode} style={affixStyle}>
                            <ResizeObserver
                                onResize={() => {
                                    this.updatePosition()
                                }}>
                                {collapsedView ? (
                                    <div
                                        className={classNames(styles.wrapper, {
                                            [styles.hasAffix]: affixStyle
                                        })}>
                                        <CollapseWrapper
                                            ref={this.saveCollapsedViewNode}
                                            collapsed={collapsed}
                                            onCollapse={this.collapse}
                                            onHandle={this.onHandle}>
                                            {clonedView}
                                        </CollapseWrapper>

                                        {children}
                                    </div>
                                ) : (
                                    children
                                )}
                            </ResizeObserver>
                        </div>
                    </div>
                </ResizeObserver>
            </>
        )
    }

    private getSubviewRect() {}
}
// just use in test
export type InternalAffixClass = Affix

const AffixFC = React.forwardRef<Affix, AffixProps>((props, ref) => {
    const { prefixCls: customizePrefixCls } = props

    const affixPrefixCls = 'ant-affix'

    const affixProps: InternalAffixProps = {
        ...props,
        affixPrefixCls
    }

    return <Affix {...affixProps} ref={ref} />
})

if (process.env.NODE_ENV !== 'production') {
    AffixFC.displayName = 'Affix'
}

export default AffixFC
